import { ColumnsType } from "antd/es/table"
import { ServiceContractPayment, ServiceContractPaymentRecordsFilters } from "src/common/models/serviceContractDebitPayment"
import { SearchableTable } from "src/components/SearchableTable"
import { ColumnsKeys, summaryColumns, TABLE_COLUMNS } from "./config"
import { SystemDescriptions } from "src/common/descriptions"
import { useDispatch, useSelector } from "react-redux"
import { useEffect, useState } from "react"
import { DateRanges } from "src/components/SearchableTable/types"
import { SystemConstants } from "src/common/constants"
import moment from "moment"
import { getContractPaymentRecordsApi, sendPaymentContractReceiptApi } from "../state/action"
import dayjs from 'dayjs'
import { RootState } from "src/state/reducer"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { GET_CONTRACT_PAYMENT_RECORDS_API, SEND_CONTRACT_PAYMENT_RECEIPT_API } from "../state/actionTypes"
import { useParams } from "react-router"
import { searchableTableUtils } from "src/components/SearchableTable/utils"
import { Button, Popconfirm, Space, Tooltip } from "antd"
import { DownloadOutlined, SendOutlined } from '@ant-design/icons';
import { Company } from "src/common/models/company"
import ContractPaymentVoucher from "./ContractPaymentVoucher"
import { Loader } from "src/components/Loader"

export type ReduxProps = {
    contractPayments?: ServiceContractPayment[],
    isGettingContractPayments: boolean
    company?: Company
    isSendingPaymentReceipt: boolean
    sendPaymentReceiptSuccess: boolean
}

interface Props {
    serviceContractId?: number
}

export const ContractPaymentComponent = (props: Props) => {
    const dispatch = useDispatch()
    const { id: customerId } = useParams<{ id: string }>();

    const handleCancel = () => {
    };

    useEffect(() => {
        fetchRecords()
    }, [])

    const [dateRanges, setDateRanges] = useState<DateRanges>({
        startDate: customerId
            ? moment().subtract(3, 'month').startOf('month').format(SystemConstants.DATETIME_QUERY(true))
            : moment().startOf('month').format(SystemConstants.DATETIME_QUERY(true)),
        endDate: moment().format(SystemConstants.DATETIME_QUERY(false)),
    })

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        contractPayments: state.contractPayment.contractPayments || [],
        isGettingContractPayments: serviceLoading(state, [GET_CONTRACT_PAYMENT_RECORDS_API]),
        company: state.common.company,
        isSendingPaymentReceipt: serviceLoading(state, [SEND_CONTRACT_PAYMENT_RECEIPT_API]),
        sendPaymentReceiptSuccess: state.contractPayment.sendPaymentReceiptSuccess,
    }))

    const handleDateRangesChange = (dateRanges: DateRanges) => {
        setDateRanges({
            startDate: moment(dateRanges.startDate).format(SystemConstants.DATETIME_QUERY(true)),
            endDate: moment(dateRanges.endDate).format(SystemConstants.DATETIME_QUERY(false)),
        })
    }

    const fetchRecords = () => {
        const payload: ServiceContractPaymentRecordsFilters = {
            dateFrom: dateRanges.startDate,
            dateTo: dateRanges.endDate,
        }
        if (props.serviceContractId) {
            payload.serviceContractId = props.serviceContractId
        }
        else if (customerId) {
            payload.customerId = Number(customerId)
        }

        dispatch(getContractPaymentRecordsApi(payload))
    }

    const descriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CONTRACT_PAYMENTS
    const commonDescriptions = SystemDescriptions.PAGES.COMMON;

    const SHOW_DOWNLOAD_ACTION_BUTTON: boolean = false;

    const tblColumns: ColumnsType<ServiceContractPayment> = [
        ...TABLE_COLUMNS,
        {
            title: descriptions.TABLE_COLUMNS.ACTIONS,
            dataIndex: ColumnsKeys.ACTIONS,
            key: ColumnsKeys.ACTIONS,
            align: searchableTableUtils.alignment.centerAlign,
            render: (_, record: ServiceContractPayment) => {
                const handleGetReceiptClick = () => {
                    if (!reduxProps.company) {
                        return
                    }

                    ContractPaymentVoucher({
                        payment: record,
                        company: reduxProps.company,
                        behavior: "SAVE",
                    })
                }

                const handleOk = () => {
                    dispatch(sendPaymentContractReceiptApi([record.id!]))
                };

                return <Space size="middle">
                    <Tooltip title={descriptions.DOWNLOAD} >
                        {
                            SHOW_DOWNLOAD_ACTION_BUTTON &&
                            <Button type="primary" ghost shape="circle" size="small"
                                icon={<DownloadOutlined />}
                                onClick={handleGetReceiptClick}
                            />
                        }
                    </Tooltip>
                    <Tooltip title={"Send Receipt"} >
                        <Popconfirm
                            placement="topLeft"
                            title={descriptions.SENDING_RECEIPT_TITLE_CONFIRMATION}
                            description={""}
                            okText={commonDescriptions.YES}
                            cancelText={commonDescriptions.NO}
                            onConfirm={handleOk}
                            onCancel={handleCancel}
                        >
                            <Button type="primary" ghost shape="circle" size="small"
                                icon={<SendOutlined />}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            },
        },
    ]

    const handleActionButtonClick = () => {
        fetchRecords()
    }

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isSendingPaymentReceipt}
            title={descriptions.SENDING_RECEIPT_TITLE}
            description={descriptions.SENDING_RECEIPT_DESCRIPTION}
        />
    )

    return (
        <>
            <SearchableTable
                showSearch
                showLoader={reduxProps.isGettingContractPayments}
                items={reduxProps.contractPayments || []}
                tableColumns={tblColumns as any}
                searchLabel={descriptions.TABLE.SEARCH_PLACEHOLDER}
                showRangePicker
                rangePickerOnChange={handleDateRangesChange}
                rangePickerDefaultValues={[
                    customerId ? dayjs().subtract(3, 'month').startOf('month') : dayjs().startOf('month'),
                    dayjs(),
                ]}
                actionButtonLabel={descriptions.TABLE.ACTION_BUTTON}
                showActionButton
                actionButtonOnClick={handleActionButtonClick}
                summaryColumns={summaryColumns}
            />
            {renderLoader()}
        </>
    )
}
